import { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Plus, X, Truck, User, Car } from '@phosphor-icons/react';
import QuotationMasterTab from '../components/QuotationMasterTab';

const SECTION_ICON_SIZE = 22;

function GlassAndWindowSection({ glassTypes, setGlassTypes, windowTypes, setWindowTypes }) {
  const [newGlassType, setNewGlassType] = useState('');
  const [newWindowType, setNewWindowType] = useState('');

  const addGlassType = async () => {
    if (!newGlassType.trim()) return;
    const updated = [...glassTypes, newGlassType.trim()];
    try {
      await api.post(`/api/dropdown-config`, { category: 'glass_type_special', values: updated });
      setGlassTypes(updated);
      setNewGlassType('');
      toast.success('Glass type added');
    } catch { toast.error('Failed to add glass type'); }
  };

  const removeGlassType = async (type) => {
    const updated = glassTypes.filter(t => t !== type);
    try {
      await api.post(`/api/dropdown-config`, { category: 'glass_type_special', values: updated });
      setGlassTypes(updated);
      toast.success('Glass type removed');
    } catch { toast.error('Failed to remove glass type'); }
  };

  const addWindowType = async () => {
    if (!newWindowType.trim()) return;
    const updated = [...windowTypes, newWindowType.trim()];
    try {
      await api.post(`/api/dropdown-config`, { category: 'upvc_window_types', values: updated });
      setWindowTypes(updated);
      setNewWindowType('');
      toast.success('Window type added');
    } catch { toast.error('Failed to add window type'); }
  };

  const removeWindowType = async (type) => {
    const updated = windowTypes.filter(t => t !== type);
    try {
      await api.post(`/api/dropdown-config`, { category: 'upvc_window_types', values: updated });
      setWindowTypes(updated);
      toast.success('Window type removed');
    } catch { toast.error('Failed to remove window type'); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Card className="border-zinc-300 rounded-sm shadow-sm">
        <CardHeader className="border-b border-zinc-300">
          <CardTitle className="text-xl font-bold text-zinc-950">Special Glass Types</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input data-testid="new-glass-type-input" value={newGlassType} onChange={(e) => setNewGlassType(e.target.value)} placeholder="Enter new glass type" className="h-11" onKeyPress={(e) => e.key === 'Enter' && addGlassType()} />
              <Button data-testid="add-glass-type-button" onClick={addGlassType} className="bg-blue-600 hover:bg-blue-700 text-white h-11"><Plus size={20} weight="bold" /></Button>
            </div>
            <div className="space-y-2">
              {glassTypes.map((type) => (
                <div key={type} data-testid={`glass-type-${type}`} className="flex items-center justify-between p-3 bg-zinc-50 border border-zinc-300 rounded-sm">
                  <span className="text-zinc-950 font-bold">{type}</span>
                  <Button data-testid={`remove-glass-type-${type}`} onClick={() => removeGlassType(type)} variant="ghost" size="sm"><X size={20} weight="bold" className="text-red-600" /></Button>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-zinc-300 rounded-sm shadow-sm">
        <CardHeader className="border-b border-zinc-300">
          <CardTitle className="text-xl font-bold text-zinc-950">UPVC Window Types</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input data-testid="new-window-type-input" value={newWindowType} onChange={(e) => setNewWindowType(e.target.value)} placeholder="Enter new window type" className="h-11" onKeyPress={(e) => e.key === 'Enter' && addWindowType()} />
              <Button data-testid="add-window-type-button" onClick={addWindowType} className="bg-blue-600 hover:bg-blue-700 text-white h-11"><Plus size={20} weight="bold" /></Button>
            </div>
            <div className="space-y-2">
              {windowTypes.map((type) => (
                <div key={type} data-testid={`window-type-${type}`} className="flex items-center justify-between p-3 bg-zinc-50 border border-zinc-300 rounded-sm">
                  <span className="text-zinc-950 font-bold">{type}</span>
                  <Button data-testid={`remove-window-type-${type}`} onClick={() => removeWindowType(type)} variant="ghost" size="sm"><X size={20} weight="bold" className="text-red-600" /></Button>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ExecutiveMasterSection({ executives, setExecutives }) {
  const [newExec, setNewExec] = useState('');

  const addExec = async () => {
    if (!newExec.trim()) return;
    const updated = [...executives, newExec.trim()];
    try {
      await api.post(`/api/masters/executives`, updated);
      setExecutives(updated);
      setNewExec('');
      toast.success('Executive added');
    } catch { toast.error('Failed to add executive'); }
  };

  const removeExec = async (name) => {
    const updated = executives.filter(e => e !== name);
    try {
      await api.post(`/api/masters/executives`, updated);
      setExecutives(updated);
      toast.success('Executive removed');
    } catch { toast.error('Failed to remove executive'); }
  };

  return (
    <Card className="border-zinc-300 rounded-sm shadow-sm">
      <CardHeader className="border-b border-zinc-300">
        <CardTitle className="text-xl font-bold text-zinc-950 flex items-center gap-2">
          <User size={SECTION_ICON_SIZE} weight="bold" className="text-blue-600" />
          Executive Names
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="space-y-4">
          <div className="flex gap-2">
            <Input data-testid="new-executive-input" value={newExec} onChange={(e) => setNewExec(e.target.value)} placeholder="Enter executive name" className="h-11" onKeyPress={(e) => e.key === 'Enter' && addExec()} />
            <Button data-testid="add-executive-button" onClick={addExec} className="bg-blue-600 hover:bg-blue-700 text-white h-11"><Plus size={20} weight="bold" /></Button>
          </div>
          <div className="space-y-2">
            {executives.map((name) => (
              <div key={name} data-testid={`executive-${name}`} className="flex items-center justify-between p-3 bg-zinc-50 border border-zinc-300 rounded-sm">
                <span className="text-zinc-950 font-bold">{name}</span>
                <Button data-testid={`remove-executive-${name}`} onClick={() => removeExec(name)} variant="ghost" size="sm"><X size={20} weight="bold" className="text-red-600" /></Button>
              </div>
            ))}
            {executives.length === 0 && <p className="text-sm text-zinc-400">No executives added yet</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function DriverMasterSection({ drivers, setDrivers }) {
  const [newName, setNewName] = useState('');
  const [newMobile, setNewMobile] = useState('');

  const addDriver = async () => {
    if (!newName.trim() || !newMobile.trim()) { toast.error('Both name and mobile are required'); return; }
    const updated = [...drivers, { name: newName.trim(), mobile: newMobile.trim() }];
    try {
      await api.post(`/api/masters/drivers`, updated);
      setDrivers(updated);
      setNewName('');
      setNewMobile('');
      toast.success('Driver added');
    } catch { toast.error('Failed to add driver'); }
  };

  const removeDriver = async (name) => {
    const updated = drivers.filter(d => d.name !== name);
    try {
      await api.post(`/api/masters/drivers`, updated);
      setDrivers(updated);
      toast.success('Driver removed');
    } catch { toast.error('Failed to remove driver'); }
  };

  return (
    <Card className="border-zinc-300 rounded-sm shadow-sm">
      <CardHeader className="border-b border-zinc-300">
        <CardTitle className="text-xl font-bold text-zinc-950 flex items-center gap-2">
          <Truck size={SECTION_ICON_SIZE} weight="bold" className="text-orange-600" />
          Drivers (Name + Mobile)
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="space-y-4">
          <div className="flex gap-2">
            <Input data-testid="new-driver-name-input" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Driver name" className="h-11 flex-1" />
            <Input data-testid="new-driver-mobile-input" value={newMobile} onChange={(e) => setNewMobile(e.target.value)} placeholder="Mobile number" className="h-11 flex-1" />
            <Button data-testid="add-driver-button" onClick={addDriver} className="bg-orange-500 hover:bg-orange-600 text-white h-11"><Plus size={20} weight="bold" /></Button>
          </div>
          <div className="space-y-2">
            {drivers.map((d) => (
              <div key={d.name} data-testid={`driver-${d.name}`} className="flex items-center justify-between p-3 bg-zinc-50 border border-zinc-300 rounded-sm">
                <div>
                  <span className="text-zinc-950 font-bold">{d.name}</span>
                  <span className="text-zinc-500 ml-3 text-sm">{d.mobile}</span>
                </div>
                <Button data-testid={`remove-driver-${d.name}`} onClick={() => removeDriver(d.name)} variant="ghost" size="sm"><X size={20} weight="bold" className="text-red-600" /></Button>
              </div>
            ))}
            {drivers.length === 0 && <p className="text-sm text-zinc-400">No drivers added yet</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function VehicleMasterSection({ vehicles, setVehicles }) {
  const [newVehicle, setNewVehicle] = useState('');

  const addVehicle = async () => {
    if (!newVehicle.trim()) return;
    const updated = [...vehicles, newVehicle.trim()];
    try {
      await api.post(`/api/masters/vehicles`, updated);
      setVehicles(updated);
      setNewVehicle('');
      toast.success('Vehicle added');
    } catch { toast.error('Failed to add vehicle'); }
  };

  const removeVehicle = async (v) => {
    const updated = vehicles.filter(x => x !== v);
    try {
      await api.post(`/api/masters/vehicles`, updated);
      setVehicles(updated);
      toast.success('Vehicle removed');
    } catch { toast.error('Failed to remove vehicle'); }
  };

  return (
    <Card className="border-zinc-300 rounded-sm shadow-sm">
      <CardHeader className="border-b border-zinc-300">
        <CardTitle className="text-xl font-bold text-zinc-950 flex items-center gap-2">
          <Car size={SECTION_ICON_SIZE} weight="bold" className="text-emerald-600" />
          Vehicle Numbers
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="space-y-4">
          <div className="flex gap-2">
            <Input data-testid="new-vehicle-input" value={newVehicle} onChange={(e) => setNewVehicle(e.target.value)} placeholder="e.g. KA-01-AB-1234" className="h-11" onKeyPress={(e) => e.key === 'Enter' && addVehicle()} />
            <Button data-testid="add-vehicle-button" onClick={addVehicle} className="bg-emerald-600 hover:bg-emerald-700 text-white h-11"><Plus size={20} weight="bold" /></Button>
          </div>
          <div className="space-y-2">
            {vehicles.map((v) => (
              <div key={v} data-testid={`vehicle-${v}`} className="flex items-center justify-between p-3 bg-zinc-50 border border-zinc-300 rounded-sm">
                <span className="text-zinc-950 font-bold">{v}</span>
                <Button data-testid={`remove-vehicle-${v}`} onClick={() => removeVehicle(v)} variant="ghost" size="sm"><X size={20} weight="bold" className="text-red-600" /></Button>
              </div>
            ))}
            {vehicles.length === 0 && <p className="text-sm text-zinc-400">No vehicles added yet</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function Settings() {
  const [glassTypes, setGlassTypes] = useState([]);
  const [windowTypes, setWindowTypes] = useState([]);
  const [executives, setExecutives] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('masters');

  const fetchAll = useCallback(async () => {
    try {
      const [glassRes, windowRes, execRes, driverRes, vehicleRes] = await Promise.all([
        api.get(`/api/dropdown-config?category=glass_type_special`),
        api.get(`/api/dropdown-config?category=upvc_window_types`),
        api.get(`/api/masters/executives`),
        api.get(`/api/masters/drivers`),
        api.get(`/api/masters/vehicles`),
      ]);
      setGlassTypes(glassRes.data.values || []);
      setWindowTypes(windowRes.data.values || []);
      setExecutives(execRes.data || []);
      setDrivers(driverRes.data || []);
      setVehicles(vehicleRes.data || []);
    } catch {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const tabs = [
    { key: 'masters', label: 'Master Data', color: 'orange' },
    { key: 'dropdowns', label: 'Dropdown Config', color: 'blue' },
    { key: 'quotation', label: 'Quotation Master', color: 'emerald' },
  ];

  return (
    <Layout>
      <div className="space-y-6 pb-20">
        <div>
          <h1 className="text-4xl font-light tracking-tight text-zinc-950">Settings</h1>
          <p className="text-sm text-zinc-500 mt-2">Manage master data and dropdown configurations</p>
        </div>

        <div data-testid="settings-tabs" className="flex gap-2 border-b border-zinc-200 pb-0">
          {tabs.map(tab => (
            <button
              key={tab.key}
              data-testid={`settings-tab-${tab.key}`}
              onClick={() => setActiveTab(tab.key)}
              className={`px-5 py-3 text-sm font-bold uppercase tracking-[0.15em] border-b-2 transition-colors ${
                activeTab === tab.key
                  ? `border-${tab.color}-500 text-${tab.color}-600`
                  : 'border-transparent text-zinc-500 hover:text-zinc-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12">Loading settings...</div>
        ) : (
          <>
            {activeTab === 'masters' && (
              <div className="space-y-6">
                <ExecutiveMasterSection executives={executives} setExecutives={setExecutives} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <DriverMasterSection drivers={drivers} setDrivers={setDrivers} />
                  <VehicleMasterSection vehicles={vehicles} setVehicles={setVehicles} />
                </div>
              </div>
            )}
            {activeTab === 'dropdowns' && (
              <GlassAndWindowSection
                glassTypes={glassTypes} setGlassTypes={setGlassTypes}
                windowTypes={windowTypes} setWindowTypes={setWindowTypes}
              />
            )}
            {activeTab === 'quotation' && (
              <QuotationMasterTab />
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
